<template lang="pug">
div.uc-upload-image.rounded-xl(:class="{ '-uploading': pendingUpload }" ref="wrapper")
  form.dz(:class="{ 'dz--show': savedURL }" ref="dropzoneTarget")
    .dz__content.border.border-2.border-dashed.min-h-full.rounded-xl.p-8.text-center.flex.flex-col.gap-4.items-center.justify-center(
      class="min-h-[30em]"
    )
      .flex.flex-col.sm_flex-row.gap-10(v-if="imgSrc")
        mixin image-container
          .p-5.rounded-xl.flex.items-center.h-auto&attributes(attributes)
            img.dz__img.upload-image-preview(
              :src="imgSrc"
              class="min-w-[10em] max-w-[20em] max-h-[10em]"
            )
        +image-container.bg-white
      .prose-sm
        uc-text.font-bold.text-2xl.text-indigo-600.-center(text="Drag and drop an image here" type="h2")
      .prose-sm
        uc-text.-center(text="PNG, JPG, SVG, or GIF / Max. 1MB" type="p")
      .-center(v-if="isScreenPatched")
        uc-text.font-bold(text="Please check that your image looks OK against the page background." type="p")
        uc-text.italic(text="If not, you can drag in a new image." type="p")

      .prose-sm
        uc-text.font-bold.text-xl.text-red-700(v-if="fileSizeError" text="Error: File size too large" type="h2")
        uc-text.font-bold.text-xl(v-if="pendingUpload" :text="`Saving image... ${percentUploaded}`")
        uc-text.font-bold.text-xl.text-green-800(v-else-if="uploadSuccess && !uploadError && !fileSizeError" text="Image saved.")
        uc-text.font-bold.text-xl(v-else-if="uploadError" text="We had a problem, please try again later.")
      .dz__border.flex.flex-row.gap-4.justify-between.items-center.bg-white
        .img-size.px-2.flex.flex-row.gap-2(v-if="imgSrc") 
          uc-text.px-4.pt-1(text="Image size:" type="div")
          .img-size-range
            select.px-2.pt-1(@change="resizeImage" v-model="selectedSize")
              option(value="default" key="default") 
                uc-text(text="Select")
              option(v-for="size in generatedSizes" :value="size.value" :key="size.value")
                uc-text(:text="size.text")
        button.p-2.btn-primary.bg-red-700.dz__delete.font-bold(
          @click="removeImage"
          v-if="isScreenPatched"
        )
          uc-icon.me-2.text-lg.text-white(icon="material-symbols:delete-forever")
          uc-text.text-white(text="Reset image")

</template>

<script lang="ts">
import Dropzone from 'dropzone';
import getValue from 'get-value';
import 'dropzone/dist/dropzone.css';
import { dump as yamlDump } from 'js-yaml';
import SCORM from '../lib/SCORM';
import { closeLightbox } from '../lib/openLightbox';
import expandUploadPath from '../lib/expandUploadPath';
import axios from 'axios';
import useContent from '../stores/useContent';
import useEditMode from '../stores/useEditMode';
import getEditorWindow from '../lib/getEditorWindow';
import { findScreenById } from '../lib/applyPatch';
import { removeScreenIndex } from '../lib/removeScreenIndex';

export default {
  setup() {
    const content = useContent();
    const { activeEditPath, activeScreenId } = useEditMode();
    return { content, activeEditPath, activeScreenId };
  },
  data() {
    return {
      pendingUpload: false,
      assetPreview: '',
      uploadSuccess: false,
      uploadError: false,
      progress: 0,
      dz: null,
      selectedSize: 'default',
      uuid: 'files' + Math.random().toString().split('.')[1],
      fileSizeError: false
    };
  },
  computed: {
    savedURL() {
      return getValue(this.content.compiled, this.activeEditPath);
    },

    isScreenPatched() {
      const originalValue = getValue(this.content.content, this.activeEditPath);
      return this.savedURL !== originalValue;
    },

    percentUploaded() {
      const percent = Math.ceil(this.progress * 100);

      return percent > 0 && percent < 100 ? `(${percent})%` : '';
    },

    activeScreenEditPath() {
      // TODO - assumtion is active edit screen props are always one level above the asset path. This may not always be the case.
      // TODO - move re-sizing to the uc-assets component after wrapping it in an editable component. Use +/- buttons to resize.
      const screenEditPathArray = this.activeEditPath.split('.');
      screenEditPathArray.pop();
      return screenEditPathArray.length > 0
        ? screenEditPathArray.join('.')
        : false;
    },

    activeScreenProps() {
      return this.activeScreenEditPath
        ? getValue(this.content.compiled, this.activeScreenEditPath)
        : false;
    },

    generatedSizes() {
      return [
        { value: '4', text: 'Tiny' },
        { value: '8', text: 'Extra small' },
        { value: '12', text: 'Small' },
        { value: '16', text: 'Medium' },
        { value: '20', text: 'Large' },
        { value: '30', text: 'Extra large' }
      ];
    },

    baseImgSize() {
      // TODO - deal with image sizes defined by different props img-size-right etc.
      return (
        getValue(
          this.content.content,
          `${this.activeScreenEditPath}.img-size`
        ) || false
      );
    },

    imageSize() {
      let imgSize = this.baseImgSize;
      if (this.activeScreenProps && this.activeScreenProps['img-size']) {
        imgSize = this.activeScreenProps['img-size'];
      }
      return imgSize;
    },

    imgSrc() {
      // only show image src if it's patched
      //if (!this.isScreenPatched) return false;

      const imgPath = this.assetPreview || this.savedURL || false;
      if (imgPath) return expandUploadPath(imgPath);
      return false;
    }
  },
  mounted() {
    if (!this.$refs.dropzoneTarget) return;

    if (this.savedURL === true) this.setAssetPatch('');

    this.selectedSize = this.imageSize || 'default';

    this.dz = new Dropzone(this.$refs.dropzoneTarget, {
      url: 'https://lms.vinciworks.com/SharedScormCourses2012/CustomScormCourses/Internal/165/upload/upload.php',
      autoProcessQueue: false,
      addedfile: file => {
        this.fileSizeError = false;
        if (file.size > 1000000) {
          this.fileSizeError = true;
          this.dz.removeFile(file);
          return;
        }
        var reader = new FileReader();
        reader.onload = e => {
          var dataURL = e.target.result;
          this.uploadImage(dataURL, file);
        };

        reader.readAsDataURL(file);
      }
    });

    this.dz.on('addedfile', file => {
      console.log('Asset added', { file });
    });
  },
  methods: {
    closeLightbox,

    // gets the content for the activeScreenId, updates the edit path property and saves to patches using content.updateValue
    setAssetPatch(url: string) {
      this.content.setPath(this.activeEditPath, url); // clear any previous asset
    },

    // VP-3886: in authoring, setPath/resetPath only update the preview overlay —
    // commit() never reaches the YAML draft, so image-only edits were lost unless
    // a text edit pushed the whole screen. Mirror uc-tiptap-author: send the
    // compiled screen to the authoring host (hook absent in classic edit mode).
    persistToDraft() {
      const hostWindow = getEditorWindow('acceptFromPreview');
      if (!hostWindow?.acceptFromPreview) return;

      // Resolve the screen from the edit path, not activeScreenId — nested
      // templates (carousel/slides) re-provide sectionId as the slide id,
      // which never matches a screen.
      const pathToScreen = this.activeEditPath?.match(
        /pages\.\d+\.screens\.\d+/
      )?.[0];
      const screen = pathToScreen
        ? getValue(this.content.compiled, pathToScreen)
        : findScreenById(this.content.compiled, this.activeScreenId);
      if (!screen?.id) {
        console.error('persistToDraft: screen not found', {
          editPath: this.activeEditPath,
          screenId: this.activeScreenId
        });
        return;
      }

      try {
        const cloned = removeScreenIndex(screen);

        hostWindow.acceptFromPreview(yamlDump(cloned, { lineWidth: -1 }));
        // discrete action: commit the draft now, deferred so the host's save
        // pipeline sees the new draft first (see uc-upload-logo setLogo)
        setTimeout(() => {
          getEditorWindow('flushInlineCommit')?.flushInlineCommit?.();
        }, 100);
      } catch (e) {
        console.error('persistToDraft: failed to push image edit to draft', e);
      }
    },

    async uploadImage(dataURL, file) {
      this.assetPreview = dataURL;
      this.pendingUpload = true;
      this.uploadError = false;
      this.progress = 0;

      try {
        const url = await this.upload(file);
        this.setAssetPatch(url);
        this.persistToDraft();
        this.uploadSuccess = true;
      } catch (e) {
        this.uploadError = true;
        console.error(e);
      }

      this.pendingUpload = false;
      this.progress = 0;
    },

    async removeImage() {
      const confirm = await window.confirm(
        'Are you sure you want to remove this image?'
      );

      if (confirm) {
        this.assetPreview = '';
        this.content.resetPath(this.activeEditPath);
        if (this.baseImgSize) {
          this.content.setPath(`${this.activeScreenEditPath}.img-size`, this.baseImgSize);
        }
        this.persistToDraft();
        this.closeLightbox();
      }
    },

    async upload(file) {
      let formData = new FormData();
      const firmCode = SCORM.getFirm() || SCORM.userName;
      formData.append('file', file);
      formData.append('username', firmCode);

      return new Promise(resolve => {
        axios
          .post(
            'https://lms.vinciworks.com/SharedScormCourses2012/CustomScormCourses/Internal/165/upload/upload.php',
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          )
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            this.uploadError = true;
            this.uploadSuccess = false;
          });
      }).catch(e => {
        this.uploadError = true;
        this.uploadSuccess = false;
      });
    },

    resizeImage() {
      if (this.selectedSize === 'default') {
        this.content.resetPath(`${this.activeScreenEditPath}.img-size`);
      } else {
        this.content.setPath(
          `${this.activeScreenEditPath}.img-size`,
          this.selectedSize
        );
      }
      this.persistToDraft();
    }
  }
};
</script>

<style>
#lightbox-upload-image .self-end button {
  @apply py-1 px-6 text-white rounded-lg focus-visible_ring-8 opacity-100 bg-indigo-500 hover_bg-indigo-900;
}

.uc-upload-image {
  background: #eff1fe;
  overflow: hidden;
}

.uc-upload-image .dz__content {
  border-color: rgba(101, 116, 205, 0.6);
}
</style>
